import { Mail, Wifi, WifiOff, Shield } from 'lucide-react';

/**
 * Sidebar footer badge showing auth provider, sync recency and session
 * expiry warnings. Renders a compact icon variant when collapsed.
 */
export function SessionHealthBadge({ sessionMeta, lastSyncTime, expanded }) {
  const isMsAuth = sessionMeta?.authProvider === 'microsoft';
  const expiresAt = sessionMeta?.sessionExpiresAt ? new Date(sessionMeta.sessionExpiresAt) : null;
  const now = new Date();
  const hoursLeft = expiresAt ? (expiresAt - now) / (1000 * 60 * 60) : null;
  const isWarning = hoursLeft !== null && hoursLeft <= 24 && hoursLeft > 0;
  const isExpired = hoursLeft !== null && hoursLeft <= 0;
  const dotColor = isExpired ? 'var(--pd-crimson)' : isWarning ? '#f59e0b' : '#22c55e';

  let syncLabel = '';
  if (lastSyncTime) {
    const diffSec = Math.floor((now - lastSyncTime) / 1000);
    if (diffSec < 10) syncLabel = 'Just now';
    else if (diffSec < 60) syncLabel = `${diffSec}s ago`;
    else syncLabel = `${Math.floor(diffSec / 60)}m ago`;
  }

  if (!expanded) {
    return (
      <div data-testid="session-health-badge-collapsed" className="flex items-center justify-center mb-2" title={`${isMsAuth ? 'Microsoft SSO' : 'Email session'} — ${syncLabel ? `Synced ${syncLabel}` : 'Connecting...'}`}>
        <div className="relative">
          {lastSyncTime
            ? <Wifi className="w-4 h-4" style={{ color: 'var(--pd-muted)' }} />
            : <WifiOff className="w-4 h-4" style={{ color: 'var(--pd-subtle)' }} />}
          <span className="absolute -bottom-0.5 -right-0.5 w-[6px] h-[6px] rounded-full" style={{ background: dotColor, border: '1.5px solid var(--pd-panel-solid)' }} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="session-health-badge" className="rounded-2xl px-3 py-2.5 mb-2" style={{ background: 'var(--pd-panel-solid)', border: '1px solid var(--pd-border)' }}>
      <div className="flex items-center gap-2.5">
        <div className="relative flex-shrink-0">
          {isMsAuth
            ? <Shield className="w-4 h-4" style={{ color: 'var(--pd-muted)' }} />
            : <Mail className="w-4 h-4" style={{ color: 'var(--pd-muted)' }} />}
          <span className="absolute -bottom-0.5 -right-0.5 w-[7px] h-[7px] rounded-full" style={{ background: dotColor, border: '1.5px solid var(--pd-panel-solid)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold" style={{ color: 'var(--pd-text)' }}>
            {isMsAuth ? 'Microsoft SSO' : 'Email session'}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            {lastSyncTime
              ? <Wifi className="w-2.5 h-2.5" style={{ color: dotColor }} />
              : <WifiOff className="w-2.5 h-2.5" style={{ color: 'var(--pd-subtle)' }} />}
            <span className="text-[9px]" style={{ color: 'var(--pd-subtle)' }}>
              {syncLabel ? `Synced ${syncLabel}` : 'Connecting...'}
            </span>
          </div>
        </div>
        {isWarning ? (
          <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: 'var(--pd-warning-soft)', color: 'var(--pd-warning)' }}>
            {Math.floor(hoursLeft)}h left
          </span>
        ) : null}
      </div>
    </div>
  );
}
